import type { VisitWindow } from '../../src/engine/visits';
import type { FamilyStop } from '../../src/publish/payload';
/** The track as the site serves it (see family/functions/_lib/views.ts). */
export type TrackView = {
  fixes: [number, number, number][];
  last: { t: number; lat: number; lon: number; sog: number | null } | null;
  region: string | null;
  state: 'underway' | 'at-anchor' | 'silent' | 'no-data';
  since: number | null;
};

/** The planned route as the site serves it (no dates): the line and each stop once, with its planner place. */
export type RouteView = { line: [number, number][]; stops: { port: string; label: string; place: string; lon: number; lat: number }[] };

/** The live plan's dates as the site serves them: every stop in sailing order and the visit windows. */
export type PlanData = { publishedAt: string; windows: VisitWindow[]; stops: FamilyStop[] };

/** Reads JSON from the site, turning a failure into a sentence a person can act on. */
export async function getJson<T>(url: string): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  let res: Response;
  try { res = await fetch(url, { credentials: 'same-origin', headers: { accept: 'application/json' } }); }
  catch { return { ok: false, status: 0, error: 'Could not reach the site. Check your connection and reload.' }; }
  const body = await res.json().catch(() => null) as (T & { error?: string }) | null;
  if (!res.ok || body === null) return { ok: false, status: res.status, error: body?.error ?? `The site answered ${res.status}. Reload to try again.` };
  return { ok: true, data: body };
}

export function ago(ms: number): string {
  const min = Math.round(ms / 60_000);
  if (min < 60) return `${Math.max(1, min)} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} days ago`;
}

export const when = (t: number) => new Date(t).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
export const day = (t: number) => new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
/** A date the server gives as UTC midnight, shown as that calendar day everywhere. */
export const utcDay = (t: number) => new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const clock = (t: number, now: number) => (now - t < 20 * 3_600_000 ? new Date(t).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : when(t));
export const isoDay = (iso: string) => utcDay(Date.parse(`${iso}T00:00:00Z`));
/** A span of days: "Until 15 Dec" for the start, one day for a stop with no stay, else "from to to". */
export const span = (from: string | null, to: string) => (from === null ? `Until ${isoDay(to)}` : from === to ? isoDay(from) : `${isoDay(from)} to ${isoDay(to)}`);

/** One plain sentence about where the boat is and how fresh that is. */
export function stateLines(v: TrackView, now: number): string[] {
  const lines: string[] = [];
  lines.push(v.region ? `Now in ${v.region}.` : v.state === 'no-data' ? 'No position yet.' : 'Now at sea.');
  if (v.state === 'silent') lines.push(`Last report ${when(v.since!)}: we're probably just offline, and the map catches up when we're back.`);
  else if (v.state === 'underway') lines.push(`Underway since ${when(v.since!)}.`);
  else if (v.state === 'at-anchor') lines.push(`At anchor since ${when(v.since!)}.`);
  if (v.last) lines.push(`Position as of ${ago(now - v.last.t)} (${clock(v.last.t, now)})${v.last.sog !== null ? `, ${v.last.sog.toFixed(1)} kn` : ''}.`);
  return lines;
}

/** Status lines that keep their ages true while the page stays open; returns a stop function. */
export function liveLines(el: HTMLElement, v: TrackView, line: (text: string) => Node): () => void {
  const draw = () => el.replaceChildren(...stateLines(v, Date.now()).map(line));
  draw();
  const timer = setInterval(draw, 60_000);
  return () => clearInterval(timer);
}
